import React, { useState } from 'react';
import { MapPin, Mail, Phone, Send, Clock, User } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import useInView from '../components/useInView';

const ContactItem = ({ item, index }) => {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        padding: '18px 20px',
        background: 'rgba(18,22,36,0.7)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '14px',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateX(0)' : 'translateX(-20px)',
        transition: `all 0.5s ease ${index * 0.1}s`,
      }}
    >
      <div style={{
        width: '44px',
        height: '44px',
        borderRadius: '11px',
        background: `${item.color}15`,
        border: `1px solid ${item.color}30`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: item.color,
        flexShrink: 0,
      }}>
        {item.icon}
      </div>
      <div>
        <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', fontWeight: 600, marginBottom: '4px' }}>
          {item.label}
        </div>
        <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
          {item.value}
        </div>
      </div>
    </div>
  );
};

const ContactsPage = () => {
  const { t } = useLang();
  const [headerRef, headerInView] = useInView();
  const [formRef, formInView] = useInView();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const contactInfo = [
    {
      icon: <MapPin size={20} />,
      label: 'Адрес',
      value: 'ул. Большая Морская, д. 67, Санкт-Петербург, 190000',
      color: '#2E5AFF',
    },
    {
      icon: <Mail size={20} />,
      label: 'Email',
      value: 'info@guap.ru',
      color: '#8A2BE2',
    },
    {
      icon: <Phone size={20} />,
      label: 'Телефон',
      value: '+7 (812) 494-70-00',
      color: '#FF3B9A',
    },
    {
      icon: <Clock size={20} />,
      label: 'Часы работы',
      value: 'Пн–Пт: 9:00 – 18:00',
      color: '#5B8AFF',
    },
    {
      icon: <User size={20} />,
      label: 'Кафедра',
      value: 'Кафедра менеджмента',
      color: '#C050FF',
    },
  ];

  return (
    <div style={{ minHeight: '100vh', paddingTop: '80px', paddingBottom: '80px' }}>
      {/* Header */}
      <div
        ref={headerRef}
        style={{
          maxWidth: '1280px',
          margin: '0 auto',
          padding: '60px 24px 48px',
          opacity: headerInView ? 1 : 0,
          transform: headerInView ? 'translateY(0)' : 'translateY(40px)',
          transition: 'all 0.8s ease',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ width: '40px', height: '2px', background: 'linear-gradient(90deg, #2E5AFF, #8A2BE2)' }} />
          <span style={{ fontSize: '12px', fontWeight: 700, color: '#2E5AFF', letterSpacing: '3px', textTransform: 'uppercase' }}>
            Contacts
          </span>
        </div>
        <h1 style={{
          fontSize: 'clamp(28px, 4vw, 48px)',
          fontWeight: 900,
          letterSpacing: '-1.5px',
          lineHeight: 1.1,
          marginBottom: '12px',
          color: '#fff',
        }}>
          {t('contacts_title')}
        </h1>
        <p style={{ fontSize: '15px', color: 'rgba(255,255,255,0.55)' }}>
          {t('contacts_sub')}
        </p>
      </div>

      {/* Content */}
      <div style={{
        maxWidth: '1280px',
        margin: '0 auto',
        padding: '0 24px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '32px',
      }}>
        {/* Contact info */}
        <div>
          {/* Info cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' }}>
            {contactInfo.map((item, i) => (
              <ContactItem key={i} item={item} index={i} />
            ))}
          </div>

          {/* Map placeholder */}
          <div style={{
            borderRadius: '16px',
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.06)',
            background: 'rgba(18,22,36,0.6)',
            height: '200px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}>
            {/* Decorative map-like grid */}
            <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0.15 }}>
              <defs>
                <pattern id="grid" width="30" height="30" patternUnits="userSpaceOnUse">
                  <path d="M 30 0 L 0 0 0 30" fill="none" stroke="rgba(46,90,255,0.5)" strokeWidth="0.5" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#grid)" />
            </svg>
            <div style={{ position: 'relative', textAlign: 'center' }}>
              <MapPin size={32} style={{ color: '#2E5AFF', marginBottom: '8px' }} />
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '12px' }}>
                ул. Большая Морская, д. 67
              </div>
              <a
                href="https://yandex.ru/maps/-/CCUqMHlZAA"
                target="_blank"
                rel="noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '8px 18px',
                  background: 'rgba(46,90,255,0.2)',
                  border: '1px solid rgba(46,90,255,0.4)',
                  borderRadius: '8px',
                  color: '#7ea4ff',
                  textDecoration: 'none',
                  fontSize: '13px',
                  fontWeight: 600,
                }}
              >
                Открыть карту
              </a>
            </div>
          </div>
        </div>

        {/* Contact form */}
        <div
          ref={formRef}
          style={{
            opacity: formInView ? 1 : 0,
            transform: formInView ? 'translateX(0)' : 'translateX(30px)',
            transition: 'all 0.7s ease 0.2s',
          }}
        >
          <div style={{
            background: 'rgba(18,22,36,0.7)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '20px',
            padding: '32px',
          }}>
            <h2 style={{
              fontSize: '20px',
              fontWeight: 800,
              color: '#fff',
              marginBottom: '8px',
              letterSpacing: '-0.5px',
            }}>
              Задайте вопрос
            </h2>
            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginBottom: '28px' }}>
              Мы ответим на ваш вопрос о курсе или поможем с записью
            </p>

            {submitted ? (
              <div style={{
                textAlign: 'center',
                padding: '40px 20px',
              }}>
                <div style={{
                  width: '64px', height: '64px',
                  borderRadius: '50%',
                  background: 'rgba(46,90,255,0.15)',
                  border: '2px solid #2E5AFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  fontSize: '28px',
                }}>
                  ✓
                </div>
                <h3 style={{ fontSize: '18px', fontWeight: 700, color: '#fff', marginBottom: '8px' }}>
                  Сообщение отправлено!
                </h3>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.5)' }}>
                  Мы свяжемся с вами в течение 1–2 рабочих дней
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  style={{
                    marginTop: '24px',
                    padding: '10px 24px',
                    background: 'rgba(46,90,255,0.15)',
                    border: '1px solid rgba(46,90,255,0.3)',
                    borderRadius: '8px',
                    color: '#7ea4ff',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 600,
                  }}
                >
                  Отправить ещё
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {[
                  { label: 'Имя', key: 'name', type: 'text', placeholder: 'Иван Иванов' },
                  { label: 'Email', key: 'email', type: 'email', placeholder: 'ivan@guap.ru' },
                ].map(field => (
                  <div key={field.key}>
                    <label style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.45)', fontWeight: 600, marginBottom: '8px', letterSpacing: '0.5px' }}>
                      {field.label}
                    </label>
                    <input
                      type={field.type}
                      placeholder={field.placeholder}
                      value={formData[field.key]}
                      onChange={e => setFormData({ ...formData, [field.key]: e.target.value })}
                      required
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '10px',
                        color: '#fff',
                        fontSize: '14px',
                        outline: 'none',
                        transition: 'border-color 0.2s ease',
                        boxSizing: 'border-box',
                      }}
                      onFocus={e => e.target.style.borderColor = 'rgba(46,90,255,0.5)'}
                      onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
                    />
                  </div>
                ))}
                <div>
                  <label style={{ display: 'block', fontSize: '12px', color: 'rgba(255,255,255,0.45)', fontWeight: 600, marginBottom: '8px' }}>
                    Сообщение
                  </label>
                  <textarea
                    placeholder="Ваш вопрос о курсе..."
                    rows={4}
                    value={formData.message}
                    onChange={e => setFormData({ ...formData, message: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '10px',
                      color: '#fff',
                      fontSize: '14px',
                      outline: 'none',
                      resize: 'vertical',
                      transition: 'border-color 0.2s ease',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                    }}
                    onFocus={e => e.target.style.borderColor = 'rgba(46,90,255,0.5)'}
                    onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.08)'}
                  />
                </div>
                <button
                  type="submit"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    padding: '14px 28px',
                    background: 'linear-gradient(135deg, #2E5AFF, #8A2BE2)',
                    border: 'none',
                    borderRadius: '10px',
                    color: '#fff',
                    fontSize: '14px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 10px 30px rgba(46,90,255,0.3)',
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 16px 40px rgba(46,90,255,0.4)';
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(46,90,255,0.3)';
                  }}
                >
                  <Send size={16} />
                  Отправить
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactsPage;
